import { useMutation } from "@tanstack/react-query";
import { format } from "date-fns";

import { caseNotesApi } from "@/api";
import { downloadFile, getFileNameFromResponse } from "@/common/helper/fileDownloadHelper";

/**
 * Used only if the server response somehow carries no Content-Disposition file name. Carries the same
 * timestamp block as the server-side name, so repeat downloads stay distinguishable even then.
 */
const fallbackArchiveFileName = () => `Akt_${format(new Date(), "yyyy-MM-dd_HHmmss")}.zip`;

/**
 * Downloads the client's complete Akt — every case note plus every document uploaded to the
 * Vermögensverzeichnis — as a single ZIP archive.
 *
 * The endpoint streams binary content, so the generated client's `…Raw` variant is used and the body
 * read as a blob — calling `downloadAllCaseNotes()` would try to parse the archive as JSON. The archive
 * name is composed server-side from case number, client name and export timestamp, so it is read back
 * from Content-Disposition rather than rebuilt here. Failures surface through the global error
 * middleware's toast.
 */
export function useCaseNoteArchiveDownload(clientId: string) {
  const mutation = useMutation({
    mutationFn: async () => {
      const response = await caseNotesApi.downloadAllCaseNotesRaw({ clientId });

      return {
        blob: await response.raw.blob(),
        fileName: getFileNameFromResponse(response.raw, fallbackArchiveFileName()),
      };
    },
    onSuccess: ({ blob, fileName }) => {
      downloadFile(blob, fileName);
    },
  });

  return { downloadArchive: () => mutation.mutate(), isDownloading: mutation.isPending };
}
